import type { PlanCategory, PlanNodeOut } from '@/client';
import { canSendPlanAction } from '@/components/maintenance/model/planActions';
import { isGroup, isWarrantyNode, planKind } from '@/components/maintenance/model/planSelectors';
import {
	isWorthDoing,
	PlanActions,
	PlanKind,
	PlanPlacements,
	PlanScheduleKinds,
	type PlanPlacement,
} from '@/components/maintenance/model/planVocabulary';

/** The rollup a compliance obligation is filtered by, beside `PlanKind.Compliance`. */
const COMPLIANCE_CATEGORY: PlanCategory = 'compliance';

/** The vet verdict a card carries until the household keeps or removes it. */
const UNREVIEWED = 'unreviewed';

/** The verdict that puts a card on one of the two surfaces. `removed` keeps it off both. */
const KEPT = 'kept';

/** The verdict that takes a card off the plan. The one state that hides a card outright. */
const REMOVED = 'removed';

/**
 * The members a group card's verdict actually lives on.
 *
 * A group holds no verdict of its own — a keep on one cascades to its members and never touches the
 * wrapper — so its `review` reads `unreviewed` however many times it has been kept. Only a child
 * with an anchor of its own can carry a verdict: a duty group's procedure steps are a checklist
 * rather than work anybody can keep, and counting those would leave the card unreviewed for good.
 *
 * Empty for a leaf, which answers for itself.
 */
const verdictMembers = (node: PlanNodeOut): PlanNodeOut[] =>
	isGroup(node) ? (node.children ?? []).filter((child) => child.action_target != null) : [];

/**
 * The verdict this card stands for — its members', where it holds none of its own.
 *
 * Kept once **any** member is: the household has engaged with the group, so a member added by a
 * later reprocess does not send the whole card back to the queue. Removed only when every member is,
 * which is the one way a group leaves the plan outright. This mirrors the roll-up the endpoint runs
 * for its own lens; the two disagreeing is what puts a card in the queue the server thinks is done.
 */
const verdictOf = (node: PlanNodeOut): string | null | undefined => {
	const members = verdictMembers(node);
	if (members.length === 0) return node.review?.status;
	if (members.some((member) => member.review?.status === KEPT)) return KEPT;
	return members.every((member) => member.review?.status === REMOVED) ? REMOVED : UNREVIEWED;
};

/**
 * Whether the household has yet to vet this card.
 *
 * `review.status` is the obligation-level verdict — `unreviewed` / `kept` / `removed` — and is the
 * field `facets.unreviewed_count` counts, so a control built on one agrees with the other.
 */
export const isUnreviewed = (node: PlanNodeOut): boolean => verdictOf(node) === UNREVIEWED;

/**
 * A person's own task is already an intentional addition, not a proposal awaiting their verdict.
 * Check both markers because the captured plans contain no user-authored card to establish which
 * one the backend reliably supplies. An explicit non-user origin wins; `custom` is only a fallback
 * when provenance is absent.
 */
export const isUserAuthored = (node: PlanNodeOut): boolean =>
	node.source_origin === 'user' || (node.source_origin == null && node.kind === 'custom');

/**
 * Work the product places, not the household.
 *
 * Warranty and compliance are fixed on the Schedule: the backend rejects their demotion with a
 * `422`, so they never fall to General — not for want of a date, and not by anyone's choice.
 */
export const isFixedOnSchedule = (node: PlanNodeOut): boolean =>
	// Both, because the two disagree: `kind` is the leaf's shape and `category` is the rollup a card
	// is filtered by, and a compliance obligation can arrive carrying either one.
	isWarrantyNode(node) || planKind(node) === PlanKind.Compliance || node.category === COMPLIANCE_CATEGORY;

/**
 * Work with no date and no interval, only a sentence about when it might apply.
 *
 * Held out of the plan for now — see {@link isReviewable} and `usePlanSlices` for the two places
 * that act on it. TODO(plan-api-as-needed): the get-plan endpoint should stop returning these,
 * which is the complete fix; it serves the notification counts as well, and those cannot be
 * corrected from here. Delete this and both call sites once it does.
 */
export const isAsNeeded = (node: PlanNodeOut): boolean => node.schedule?.kind === PlanScheduleKinds.AsNeeded;

/**
 * Whether a card is genuinely waiting for the person to keep or dismiss it.
 *
 * Work the product places is not: asking whether to keep a compliance obligation offers a verdict
 * the backend then refuses, the card being fixed on the Schedule either way.
 *
 * Nor is as-needed work. It carries no date to schedule against and no interval to correct, so the
 * pass could only ever ask a yes/no about a sentence — and on the captured properties it is most of
 * what the pass contains: 39 of house 56's 40 proposals, 25 of house 54's 50.
 */
export const isReviewable = (node: PlanNodeOut): boolean =>
	isUnreviewed(node) &&
	!isFixedOnSchedule(node) &&
	!isUserAuthored(node) &&
	!isAsNeeded(node) &&
	canSendPlanAction(node, PlanActions.Keep);

/**
 * Which surface a card sits on, or null while nobody has kept it.
 *
 * Read, never derived. The endpoint types the field as a plain string, so anything outside the two
 * known values is treated as no answer rather than sorted into a tab on a guess.
 */
export const placementOf = (node: PlanNodeOut): PlanPlacement | null => {
	const members = verdictMembers(node);
	if (members.length > 0) {
		// Schedule wins: one member the household put on their schedule puts the whole card there,
		// since that is the surface they asked to be reminded on.
		const placements = new Set(members.map(placementOf));
		if (placements.has(PlanPlacements.Schedule)) return PlanPlacements.Schedule;
		return placements.has(PlanPlacements.General) ? PlanPlacements.General : null;
	}

	const placement = node.review?.placement;
	return placement === PlanPlacements.Schedule || placement === PlanPlacements.General ? placement : null;
};

/**
 * A kept card the household has not put on their schedule.
 *
 * Read from the card rather than handed down, so the plan draws a suggestion as one wherever it
 * appears instead of only where a host thought to say so. `worth_doing` is only ever a passed
 * General one-off, and counts whether or not the read sent a placement with it.
 */
export const isSuggestion = (node: PlanNodeOut): boolean =>
	placementOf(node) === PlanPlacements.General || isWorthDoing(node.status);

/**
 * Whether this card can be put on the Schedule at all.
 *
 * A reminder counts back from a due date, so a card without one has nothing to fire against and the
 * endpoint answers the election with a 422. The rule binds only the household's choice — the
 * server's own default is free to place an undated card wherever it likes, and the read is believed
 * either way.
 */
export const canElectToSchedule = (node: PlanNodeOut): boolean => Boolean(node.next_due);

/** Whether the household has put this card in their plan, which is what gives it a surface. */
export const isKept = (node: PlanNodeOut): boolean => verdictOf(node) === KEPT;

/** Whether the household has taken this card off their plan. */
export const isRemoved = (node: PlanNodeOut): boolean => verdictOf(node) === REMOVED;

/**
 * Which surface to draw this card on — the whole answer for one card, including "neither".
 *
 * Only two things earn "neither", and both are reachable from somewhere else. A **removed** card is
 * off the plan by the household's own verdict. A card still **awaiting** that verdict is answered in
 * the review pass, which is where it lives until it is.
 *
 * **Everything else lands somewhere**, falling back to the Schedule when the read carries no
 * placement. That is not a derivation standing in for the server's: it is the answer to "on the plan,
 * but where?", which has to be a tab rather than nowhere. Two ways a card reaches this point:
 *
 * - it was kept, and the placement simply has not been written yet — observed against a real property
 *   on 2026-08-30, where all nine kept cards came back `null` and the plan rendered as two empty tabs;
 * - the review pass will never offer it a verdict at all. A warranty expiry is not a proposal, and a
 *   task the household wrote is already an intentional addition, so `isReviewable` excludes both.
 *   Requiring `kept` to earn a tab left those on neither surface and in no queue — a warranty-heavy
 *   property stranded 24 of them, and a task created by hand vanished the moment it was made.
 *
 * Schedule rather than General because it is the tab a plain link opens and where these cards were
 * before placement existed, so the fallback changes nothing until real values arrive.
 *
 * **The fallback asks for a date first.** A reminder counts back from a due date, so a dateless card
 * defaulting onto the Schedule would sit under a promise nothing can keep for it. Undated work falls
 * back to General, which promises nothing.
 */
/**
 * Whether the card belongs on a plan surface at all, whatever shape of work it is.
 *
 * Deliberately not `isKept`: a card the household has not answered yet is still theirs to see, and
 * requiring a verdict stranded the cards that never get one — see `surfaceOf` below. Dated work
 * asks this through `surfaceOf`; as-needed work has no placement to read and asks it directly.
 */
export const isOnPlan = (node: PlanNodeOut): boolean => !isRemoved(node) && !isReviewable(node);

export const surfaceOf = (node: PlanNodeOut): PlanPlacement | null => {
	if (!isOnPlan(node)) return null;
	const placement = placementOf(node);
	if (placement) return placement;
	if (isFixedOnSchedule(node)) return PlanPlacements.Schedule;
	return canElectToSchedule(node) ? PlanPlacements.Schedule : PlanPlacements.General;
};

/** The cards on one surface. A card nobody has kept is on neither. */
export const onPlacement = (nodes: readonly PlanNodeOut[], placement: PlanPlacement): PlanNodeOut[] =>
	nodes.filter((node) => surfaceOf(node) === placement);
